// src/components/EvaluationConfigurationStepThree.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Paper,
  Grid,
  Autocomplete,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
} from '@mui/material';
import {
  Group as GroupIcon,
  Person as PersonIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { styled } from '@mui/system';
import api from '../config/api';
import useStore from '../config/store';
import { useTranslation } from 'react-i18next';
import EvaluationAlert from './EvaluationAlert';
import { ASSESSMENT_MODES } from './assessmentMode';

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const EvaluationConfigurationStepThree = ({ selectedPosition, competenciesConfig, candidatesConfig, onCandidatesChange, mode }) => {
  // Estados
  const [applicants, setApplicants] = useState([]);
  const [selectedApplicant, setSelectedApplicant] = useState(null);
  const [addedCandidates, setAddedCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const { authToken, showAlert } = useStore.getState();
  const { t } = useTranslation();

  // Completo: solo postulantes con informe psicolaboral. Simple: cualquier
  // postulante del proyecto, incluido el banco de talento (PLAN_UNIFICACION_ASSESSMENT.md).
  const isSimpleMode = mode === ASSESSMENT_MODES.SIMPLE;

  // API Function para cargar applicants
  const getAllApplicants = async () => {
    const { project, organization } = useStore.getState();
    const response = isSimpleMode
      ? await api.get("/applicants", {
        params: { projectId: project, organizationId: organization },
        headers: { Authorization: `Bearer ${authToken}` },
      })
      : await api.get("/applicants/with-report", {
        params: { projectId: project },
        headers: { Authorization: `Bearer ${authToken}` },
      });
    return response.data;
  };

  const loadApplicants = async () => {
    try {
      setLoading(true);
      const response = await getAllApplicants();
      if (response.response && response.response.status_response === 'OK') {
        const unique = Object.values(
          (response.response.data || []).reduce((acc, app) => {
            const key = app.email || app.id;
            if (!acc[key]) acc[key] = app;
            return acc;
          }, {})
        );
        console.log('Raw data count:', response.response.data?.length);
        console.log('Unique count:', unique.length);
        console.log('Sample:', response.response.data?.slice(0, 3).map(a => ({ id: a.id, email: a.email, name: a.name })));

        setApplicants(unique.sort((a, b) => (a.name || '').localeCompare(b.name || '', 'es', { sensitivity: 'base' })));
        setError(null);
      } else {
        setError(t('applicants.loadError'));
        showAlert(t('applicants.loadError'), "warning");
      }
    } catch (err) {
      setError(t('generarApiError'));
      showAlert(t('generalApiError'), "error");
      console.error('Error loading applicants:', err);
    } finally {
      setLoading(false);
    }
  };

  // Cargar applicants al montar
  useEffect(() => {
    loadApplicants();
  }, []);

  // Cargar candidatos existentes si existen
  useEffect(() => {
    if (candidatesConfig && candidatesConfig.candidates && candidatesConfig.candidates.length > 0) {
      setAddedCandidates(candidatesConfig.candidates);
    }
  }, [candidatesConfig]);

  // Notificar cambios al componente padre
  useEffect(() => {
    const isValid = addedCandidates.length >= 1;
    onCandidatesChange({
      candidates: addedCandidates,
      isValid,
      totalSelected: addedCandidates.length
    });
  }, [addedCandidates]);

  // Función para agregar candidato
  const handleAddCandidate = () => {
    if (!selectedApplicant) {
      showAlert(t('evaluations.pleaseSelectApplicant'), 'warning');
      return;
    }

    // Verificar si ya está agregado
    if (addedCandidates.some(candidate => candidate.id === selectedApplicant.id)) {
      showAlert(t('evaluations.applicantAlreadyAdded'), 'warning');
      return;
    }

    // Agregar candidato sin estado
    const newCandidate = {
      ...selectedApplicant
    };

    setAddedCandidates(prev => [...prev, newCandidate]);
    setSelectedApplicant(null); // Limpiar selección
    showAlert(t('evaluations.applicantAddedSuccessfully'), 'success');
  };

  // Función para eliminar candidato
  const handleRemoveCandidate = (candidateId) => {
    setAddedCandidates(prev => prev.filter(candidate => candidate.id !== candidateId));
    showAlert(t('evaluations.applicantDeleted'), 'info');
  };

  // Filtrar candidatos disponibles (que no estén ya agregados)
  const availableCandidates = applicants.filter(applicant =>
    !addedCandidates.some(added => added.id === applicant.id)
  ).sort((a, b) => (a.name || '').localeCompare(b.name || '', 'es', { sensitivity: 'base' }));

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" my={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={loadApplicants}>
            <RefreshIcon sx={{ mr: 1 }} />
            {t('retry')}
          </Button>
        }
      >
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
        <GroupIcon sx={{ mr: 1, color: 'primary.main' }} />
        {t('evaluations.applicantManagement')}
      </Typography>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        {t('evaluations.applicantManagementSubTitle')} <strong>{selectedPosition?.name}</strong>.
      </Typography>

      {/* Información del cargo y competencias */}
      {selectedPosition && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            <strong>{t('evaluations.currentConfiguration')}:</strong>
          </Typography>
          <Typography variant="body2">
            • {t('position')}: {selectedPosition.name} - {selectedPosition.industria}
          </Typography>
          <Typography variant="body2">
            • {t('evaluations.configuredCapabilities')}: {competenciesConfig?.capabilities?.length || 0}
          </Typography>
          <Typography variant="body2">
            • {t('evaluations.totalPercentage')}: {competenciesConfig?.totalPercentage || 0}%
          </Typography>
        </Alert>
      )}

      {/* NUEVO: Alert de assessments disponibles */}
      <EvaluationAlert
        pendingCount={addedCandidates.length}
        showDetailedInfo={addedCandidates.length > 0}
      />

      {/* Sección Agregar Nuevo Candidato */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
          <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
          {t('evaluations.addNewApplicant')}
        </Typography>

        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={8}>
            <Autocomplete
              options={availableCandidates}
              getOptionLabel={(option) => option.name || ''}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              value={selectedApplicant}
              onChange={(event, newValue) => setSelectedApplicant(newValue)}
              renderInput={(params) => (
                <TextField {...params} label={t('evaluations.selectApplicant')} fullWidth />
              )}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <Box>
                    <Typography variant="body1">{option.name || t('comparisonDashboard.noName')}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.email || t('noEmail')}
                    </Typography>
                  </Box>
                </li>
              )}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddCandidate}
              disabled={!selectedApplicant || availableCandidates.length === 0}
              fullWidth
            >
              {t('evaluations.addApplicant')}
            </Button>
          </Grid>
        </Grid>

        {availableCandidates.length === 0 && applicants.length > 0 && (
          <Alert severity="info" sx={{ mt: 2 }}>
            {t('evaluations.allApplicantsAdded')}
          </Alert>
        )}

        {applicants.length === 0 && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {isSimpleMode ? t('evaluations.noApplicantsSimple') : t('evaluations.noApplicantsWithReport')}
          </Alert>
        )}
      </Paper>

      {/* Tabla de candidatos agregados */}
      {addedCandidates.length > 0 && (
        <Paper sx={{ mb: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" p={2}>
            <Typography variant="h6">
              {t('drawer.applicants')} ({addedCandidates.length})
            </Typography>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>{t('name')}</strong></TableCell>
                  <TableCell><strong>{t('email')}</strong></TableCell>
                  <TableCell><strong>{t('phone')}</strong></TableCell>
                  <TableCell><strong>{t('actions')}</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {addedCandidates.map((candidate, index) => (
                  <StyledTableRow key={candidate.id}>
                    <TableCell>{candidate.name || t('comparisonDashboard.noName')}</TableCell>
                    <TableCell>{candidate.email || t('noEmail')}</TableCell>
                    <TableCell>{candidate.phone || ''}</TableCell>
                    <TableCell>
                      <IconButton
                        color="error"
                        size="small"
                        onClick={() => handleRemoveCandidate(candidate.id)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </StyledTableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Resumen */}
      {addedCandidates.length > 0 && (
        <Paper sx={{ p: 2, backgroundColor: addedCandidates.length >= 1 ? '#e8f5e8' : '#fff3cd', border: `1px solid ${addedCandidates.length >= 1 ? '#4caf50' : '#ff9800'}` }}>
          <Typography variant="subtitle2" gutterBottom color="text.primary">
            <strong>{t('summary')}:</strong>
          </Typography>
          <Typography variant="body2" color="text.primary">
            • {t('evaluations.totalApplicantsAdded')}: <strong>{addedCandidates.length}</strong>
          </Typography>
          <Typography variant="body2" color={addedCandidates.length >= 1 ? 'success.main' : 'warning.main'}>
            • {t('statusWord')}: {addedCandidates.length >= 1 ? t('evaluations.readyToContinue') : t('evaluations.addAtleastOneApplicant')}
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default EvaluationConfigurationStepThree;